import { ActivityCategory, MarketingGoalTargets, zeroTargets } from "@sp-crm/core";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { trpc } from "components/rpc/trpc";
import {
    ActivityTypeOption,
    useActivityTypeOptionsState,
} from "components/tasks/activity-type-options";
import { useFeature, useNow } from "store/selectors/hooks";
import { computeMetricProgress, MetricProgress } from "./metric-progress-calc";
import { currentYearMonth, daysInYearMonth } from "./pace";

export const otherActivitiesLabel = "Other Activities";

export type TrackedMetric = "visit" | "otherTouchpoints";

export const trackedMetrics: TrackedMetric[] = ["visit", "otherTouchpoints"];

export const trackedMetricLabels: Record<TrackedMetric, string> = {
    visit: "Visits",
    otherTouchpoints: otherActivitiesLabel,
};

const categoriesForTrackedMetric: Record<TrackedMetric, ActivityCategory[]> = {
    visit: ["visit"],
    otherTouchpoints: ["call", "sms", "email", "other"],
};

export const trackedMetricTarget = (
    goal: MarketingGoalTargets,
    options: ActivityTypeOption[],
    metric: TrackedMetric,
): number => {
    const categories = new Set(categoriesForTrackedMetric[metric]);
    const categoryByTypeId = new Map<string, ActivityCategory>(
        options.map(option => [option.id, option.category]),
    );
    return Object.entries(goal.activity).reduce((sum, [taskTypeId, target]) => {
        const category = categoryByTypeId.get(taskTypeId);
        return category && categories.has(category) ? sum + target : sum;
    }, 0);
};

export interface MetricProgressResult {
    isLoading: boolean;
    isError: boolean;
    progress: MetricProgress | null;
}

export const useMetricProgress = (metric: TrackedMetric): MetricProgressResult => {
    const now = useNow();
    const marketingPlaybookEnabled = useFeature("marketingPlaybook");
    const trpcClient = trpc.useTRPC();
    const { options, isLoading: optionsLoading } = useActivityTypeOptionsState();
    const month = currentYearMonth(now);
    const showingOtherTouchpoints = metric === "otherTouchpoints";

    const attainmentQuery = useQuery({
        ...trpcClient.marketingGoal.myAttainment.queryOptions({ month }),
    });

    const goal = attainmentQuery.data?.goal ?? zeroTargets();
    const target = trackedMetricTarget(goal, options, metric);
    const hasGoal = target > 0;

    const projectionQuery = useQuery({
        ...trpcClient.marketingPlaybook.myTouchpointVolumeProjectionTotal.queryOptions({
            month,
        }),
        enabled: marketingPlaybookEnabled && hasGoal,
    });
    const itemsQuery = useQuery({
        ...trpcClient.marketingGoal.myMetricItems.queryOptions({
            month,
            categories: categoriesForTrackedMetric[metric],
            outcomeMetrics: [],
        }),
        placeholderData: keepPreviousData,
    });

    const isLoading =
        attainmentQuery.isLoading ||
        itemsQuery.isLoading ||
        optionsLoading ||
        (hasGoal && projectionQuery.isLoading);
    const isError =
        attainmentQuery.isError ||
        itemsQuery.isError ||
        (hasGoal && projectionQuery.isError);

    if (isLoading || isError) {
        return { isLoading, isError, progress: null };
    }

    const projectedMonthly = showingOtherTouchpoints
        ? (projectionQuery.data?.otherTouchpointsVolume ?? 0)
        : (projectionQuery.data?.visitVolume ?? 0);

    const completed = itemsQuery.data?.total ?? 0;
    const daysInMonth = daysInYearMonth(now);
    const progress: MetricProgress = computeMetricProgress(
        completed,
        target,
        projectedMonthly,
        daysInMonth,
        now.getDate(),
    );
    return { isLoading: false, isError: false, progress };
};
